/**
 * @file LanguagesPage.tsx
 * @brief Super Admin → Languages — toggle which of the supported
 *        languages (and locales) the platform exposes to clients.
 *
 * @details
 * Three regions:
 *   1. **Summary strip** — total / enabled / disabled counts. Helps
 *      the operator see at a glance how many languages they've turned
 *      off (the catalogue is 100+ entries long).
 *   2. **Filter bar** — flush GridCells; search across code, locale,
 *      label. The status filter (All / Enabled / Disabled) is a header
 *      pill (01-10-2026).
 *   3. **Rows table** — per language row with `<Switch>`. Optimistic
 *      flip via `useUpdateLanguageStatus` so the UI feels instant.
 *      Last-updated timestamp + updated-by chip on the right.
 *
 * @dependencies
 * - features/languages/{api,schemas}
 * - components/ui/{Input,HeaderFilterMenu,Switch,Badge,Skeleton}
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — end-to-end GridCell filters
 * @updated 01-10-2026: the status filter became a header pill
 */

import { useMemo, useState } from "react";
import { Earth, LoaderCircle } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import type { SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { Skeleton } from "@/components/ui/Skeleton";
import { Switch } from "@/components/ui/Switch";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { useLanguages, useUpdateLanguageStatus } from "./api";
import type { LanguageRow } from "./schemas";

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------
function formatUpdated(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type StatusFilter = "all" | "enabled" | "disabled";

const STATUS_ITEMS: SearchableMenuItem[] = [
  { value: "all", label: "All languages" },
  { value: "enabled", label: "Enabled only" },
  { value: "disabled", label: "Disabled only" },
];

export function LanguagesPage() {
  const { data, isLoading, isError } = useLanguages();
  const update = useUpdateLanguageStatus();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");

  // --------------------------------------------------------------------
  // Derived rows — apply filters client-side because the catalogue is
  // bounded (~150 rows max) and the network roundtrip would dominate.
  // `rows` is memoised so the empty-array fallback identity stays
  // stable across renders and downstream useMemos behave. (26-05-2026)
  // --------------------------------------------------------------------
  const rows = useMemo(() => data?.languages ?? [], [data?.languages]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (status === "enabled" && !r.enabled) return false;
      if (status === "disabled" && r.enabled) return false;
      if (!q) return true;
      const hay = `${r.code} ${r.locale ?? ""} ${r.label}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, status]);

  const totals = useMemo(() => {
    let enabled = 0;
    let disabled = 0;
    for (const r of rows) {
      if (r.enabled) enabled++;
      else disabled++;
    }
    return { total: rows.length, enabled, disabled };
  }, [rows]);

  // --------------------------------------------------------------------
  // Handlers
  // --------------------------------------------------------------------
  const handleToggle = async (row: LanguageRow, next: boolean) => {
    try {
      await update.mutateAsync({
        code: row.code,
        locale: row.locale ?? "",
        enabled: next,
        reason: next ? "Re-enabled from web panel" : "Disabled from web panel",
      });
      toast({
        variant: "success",
        title: next ? "Language enabled" : "Language disabled",
        description: `${row.label}${row.locale ? ` (${row.locale})` : ""}`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-72">
            <h2 className="text-xl font-semibold tracking-tight">Languages</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Toggle which languages and locales the platform serves to clients.
              Changes take effect immediately for new sessions.
            </p>
          </div>
          {/* The filter, top right (DES 01-10-2026). */}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <HeaderFilterMenu
              title="Filter by status"
              searchPlaceholder="Search statuses"
              items={STATUS_ITEMS}
              selected={status}
              label={
                STATUS_ITEMS.find((i) => i.value === status)?.label ??
                STATUS_ITEMS[0].label
              }
              active={status !== "all"}
              onClear={() => setStatus("all")}
              onSelect={(v) => setStatus(v as StatusFilter)}
            />
          </div>
        </GridBand>

        <GridCells className="grid-cols-1 sm:grid-cols-3">
          <SummaryTile label="Total" value={totals.total} />
          <SummaryTile label="Enabled" value={totals.enabled} tone="success" />
          <SummaryTile
            label="Disabled"
            value={totals.disabled}
            tone="destructive"
          />
        </GridCells>

        <GridBand className="flex items-center gap-2 text-sm font-medium">
          <Earth className="h-4 w-4 text-primary" />
          Catalogue
          <span className="font-normal text-muted-foreground">
            · Showing {filtered.length} of {rows.length}
          </span>
        </GridBand>

        <GridCell padded={false}>
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-sm text-destructive">
              Couldn't load languages.
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No languages match your filters.
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {filtered.map((row) => (
                <div
                  key={`${row.code}|${row.locale ?? ""}`}
                  className="flex items-center gap-4 px-4 py-4 md:px-5"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold uppercase text-brand-soft-foreground">
                    {row.code}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium">
                        {row.label}
                      </p>
                      {row.locale ? (
                        <Badge variant="muted" className="font-normal">
                          {row.locale}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      Updated {formatUpdated(row.updated_at)}
                      {row.updated_by ? ` · ${row.updated_by}` : ""}
                    </p>
                  </div>
                  <Switch
                    checked={row.enabled}
                    onCheckedChange={(next) => handleToggle(row, next)}
                    disabled={update.isPending}
                    aria-label={`Toggle ${row.label}${row.locale ? ` (${row.locale})` : ""}`}
                  />
                </div>
              ))}
            </div>
          )}

          {update.isPending ? (
            <div className="flex items-center justify-center gap-2 border-t border-border/60 py-2 text-xs text-muted-foreground">
              <LoaderCircle className="h-3 w-3 animate-spin" />
              Saving…
            </div>
          ) : null}
        </GridCell>
      </GridBlock>

      {/* The Notes screen's search (DES 01-10-2026), docked at the bottom
          like Notes. */}
      <PageDock>
        <ExpandingSearch
          value={search}
          onChange={(next) => setSearch(next)}
          placeholder="Search by code, locale, or name"
          label="Search languages"
        />
      </PageDock>
    </div>
  );
}

// ----------------------------------------------------------------------
// Summary tile
// ----------------------------------------------------------------------
interface SummaryTileProps {
  label: string;
  value: number;
  tone?: "success" | "destructive" | "default";
}

function SummaryTile({ label, value, tone = "default" }: SummaryTileProps) {
  const toneClass =
    tone === "success"
      ? "text-success"
      : tone === "destructive"
        ? "text-destructive"
        : "text-foreground";
  return (
    <GridCell>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</p>
    </GridCell>
  );
}
